import { TooltipProvider } from "@/components/ui/tooltip";
import { FilterChip } from "./FilterChip";
import { AddFilterPopover } from "./AddFilterPopover";
import { AddSortButton } from "./AddSortButton";
import { SortChip } from "./SortChip";
import type { ActiveFilter, SortSpec } from "./filterTypes";
import type {
  FieldOption,
  FilterCondition,
} from "./AdvancedFilters";
import type { PipelineStatus, ProjectMember } from "@/api/types";

interface FilterChipsBarProps {
  activeFilters: ActiveFilter[];
  /** Resolve a chip's underlying field metadata. Returns null for
   *  unknown / hidden fields — those chips render as non-editable
   *  but still carry × to remove. */
  resolveField: (fieldKey: string) => FieldOption | null;
  /** Resolve a chip's underlying FilterCondition for the editor.
   *  For tab/status/score chips this is synthesised by the parent
   *  from page-level state. For advanced chips it's the matching
   *  row in advancedFilters. */
  resolveCondition: (filter: ActiveFilter) => FilterCondition | null;
  /** Picker fields — already pre-filtered (PICKER_HIDDEN_FIELDS
   *  removed, LABEL_OVERRIDES applied). */
  pickerFields: FieldOption[];
  slug: string;
  members: ProjectMember[];
  pipelineStatuses: PipelineStatus[];
  /** Multi-sort state. Rendered as a SortChip at the LEFT of the
   *  chips row before any filter chip. Empty list → no SortChip. */
  sorts: SortSpec[];
  onSortsChange: (next: SortSpec[]) => void;
  onEditFilter: (
    filter: ActiveFilter,
    next: { operator: string; value: unknown },
  ) => void;
  onRemoveFilter: (filter: ActiveFilter) => void;
  onAddFilter: (next: {
    field: string;
    operator: string;
    value: unknown;
  }) => void;
}

/** Single horizontal row of (optional) sort chip + filter chips +
 *  the inline "+ Add filter" button at the end. Hidden entirely
 *  when there are no filters AND no sorts (the toolbar above
 *  carries its own "+ Add filter" zero-state button). */
export function FilterChipsBar({
  activeFilters,
  resolveField,
  resolveCondition,
  pickerFields,
  slug,
  members,
  pipelineStatuses,
  sorts,
  onSortsChange,
  onEditFilter,
  onRemoveFilter,
  onAddFilter,
}: FilterChipsBarProps) {
  if (activeFilters.length === 0 && sorts.length === 0) return null;

  return (
    // Single provider for the whole bar — every chip and the
    // remove button share the same delay timer so hovering from
    // one chip's tooltip to a sibling chip's tooltip doesn't
    // re-trigger the open animation. 350ms matches the
    // "intentional hover" threshold from the spec.
    <TooltipProvider delayDuration={350}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        {/* Sort chip sits BEFORE the filter chips so the operator
         *  reads "Sorted by X · is_client is false · …" in the
         *  natural order. Hidden when no sorts (own check). */}
        <SortChip sorts={sorts} onChange={onSortsChange} />
        {activeFilters.map((filter) => (
          <FilterChip
            key={filter.id}
            filter={filter}
            field={resolveField(filter.fieldKey)}
            editableCondition={resolveCondition(filter)}
            slug={slug}
            members={members}
            pipelineStatuses={pipelineStatuses}
            onEditCommit={(next) => onEditFilter(filter, next)}
            onRemove={() => onRemoveFilter(filter)}
          />
        ))}
        <AddFilterPopover
          fields={pickerFields}
          slug={slug}
          members={members}
          pipelineStatuses={pipelineStatuses}
          onAdd={onAddFilter}
          variant="chip-bar"
        />
        {/* F4: permanent "Sort" entry point next to "+ Add filter".
         *  Visible inside the chips bar regardless of how many sorts
         *  are active — symmetric with "+ Add filter". When zero
         *  sorts are active, the toolbar-variant of this button in
         *  UnifiedFilterRow handles discovery (since the chips bar
         *  is null when both filters AND sorts are empty). */}
        <AddSortButton
          sorts={sorts}
          onSortsChange={onSortsChange}
          variant="chip-bar"
        />
      </div>
    </TooltipProvider>
  );
}
